import { TAddon, TCartItem, TInventory, TProduct, TStore, TVariant } from '@components/types'
import { getFirst, getImageObject, humanizeCurrency, isStockQuantityExceeded, modalPriceCalculation, sanityIoImageLoader, stockQuantityErrorMessage, trimToLength } from '@core/utils'
import { cartState } from '@recoil/atoms'
import { variantState } from '@recoil/atoms/variant'
import { message } from 'antd'
import { motion } from 'framer-motion'
import { Check, Minus, Pencil, Plus, X } from 'lucide-react'
import Image from 'next/image'
import React, { useEffect, useState } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import { inventoryByVariantId } from './variantSelector'
import { selectedStoreState } from '@recoil/atoms/selectedstore'

interface Props {
    showModal: boolean,
    product: TProduct,
    btnName?: string,
    inventory?: TInventory,
    quantity?: number
}

const Variant: React.FC<Props> = ({ product, showModal, inventory }) => {
    const [variant, setVariant] = useState<TVariant>();
    const [Isvariant, setIsVariant] = useRecoilState(variantState);
    const [cart, setCart] = useRecoilState(cartState);
    const variantId = getFirst(inventoryByVariantId(variant, product))?.variant_id || inventory?.variant_id;
    const [productQyt, setProductQyt] = useState(variantId != undefined && cart.get(variantId.toString())?.quantity || 1)
    const [addons, setAddons] = useState<TAddon[]>([])
    const [addonList, setAddonList] = useState<TAddon[]>([]);
    const [customizedItems, setCustomizedItems] = useState([])
    const [selectedItem, setSelectedItem] = useState<TCartItem>(null)
    const selectedStore = useRecoilValue<TStore>(selectedStoreState);
    const shouldApplyInventoryLimit = selectedStore?.storemeta?.settings?.inventory_limit === true;

    useEffect(() => {
        if (selectedItem != null) {
            setProductQyt(selectedItem?.quantity)
            setAddonList(selectedItem?.addon)
        } else {
            setProductQyt(1)
            setAddonList([])
        }

        console.debug(selectedItem)
    }, [selectedItem])

    useEffect(() => {
        if (inventory != undefined) {
            setVariant(inventory?.variant)
        } else {
            setVariant(product?.variants?.find((variant) => (getFirst(inventoryByVariantId(variant, product))?.is_available == true)) as TVariant)
        }
    }, [inventory, product])



    const toggleAddon = (addOn: TAddon) => {
        let otherAddons: TAddon[] = addonList?.filter((x) => x?.addon_id !== addOn?.addon_id);
        if ((otherAddons?.length || 0) != (addonList?.length || 0)) {
            setAddonList(otherAddons);
        } else {
            setAddonList([...otherAddons, addOn])
        }
    };


    const onClickVariant = (value: TVariant) => {
        setVariant(value);
    }


    const onAddToCart = (product: TProduct, addons: TAddon[], inventory: TInventory, productQyt: number, internalId: string = null) => {
        console.debug(product, addons, inventory, productQyt, internalId)
        const finalId = `${inventory?.variant?.id}${addons.length > 0 ? "_" + addons.sort((a, b) => a.id - b.id).map((x) => x.id).join("_") : ""}`
        const _internalId = (internalId?.length || 0) > 0 ? internalId : finalId;
        if (shouldApplyInventoryLimit && isStockQuantityExceeded(cart, inventory, productQyt, [_internalId, finalId])) {
            message?.error(stockQuantityErrorMessage(inventory));
            return;
        }
        setIsVariant({ Isvariant: false, product: product, showModal: showModal, quantity: productQyt });
        (variantId !== null && variantId !== undefined) && setCart((cart) => {
            if (cart.has(_internalId)) {
                let newQuantity = productQyt;
                if (newQuantity == 0 || newQuantity == undefined) {
                    cart.delete(_internalId)
                }
                else {
                    if (finalId != _internalId) {
                        cart.delete(_internalId)
                    }
                    cart.set(finalId, { internalId: finalId, product: product, quantity: newQuantity, inventory: inventory, deliverable: inventory?.store?.is_delivery || false, addon: addonList })
                }
            } else {
                cart.set(finalId || '', { internalId: finalId, product: product, quantity: productQyt, inventory: inventory, deliverable: inventory?.store?.is_delivery || false, addon: addonList })
            }
            window?.fbq('track', 'AddToCart', {
                content_name: `${product?.name || inventory?.product?.name}`,
                content_category: `${product?.category?.name || inventory?.product?.category?.name}`,
                content_ids: [`${finalId || product?.id || inventory?.id}`],
                content_type: 'product',
                value: inventory?.price || product?.mrp,
                currency: 'RS'
            });
            return new Map(cart);
        })
    }

    useEffect(() => {
        setProductQyt(variantId != undefined && cart.get(variantId.toString())?.quantity || 1)
        setAddons(inventory?.addons || getFirst(inventoryByVariantId(variant, product))?.addons)
        setCustomizedItems(Array.from(cart.values()).filter((x) => x?.inventory?.id == (inventory?.id || getFirst(inventoryByVariantId(variant, product))?.id)))
        setAddonList([])
    }, [variant, product])

    const increase = (quantity: number) => {
        const selectedInventory = inventory || getFirst(inventoryByVariantId(variant, product)) as TInventory;
        const nextQuantity = productQyt + quantity;
        const finalId = `${selectedInventory?.variant?.id}${addonList.length > 0 ? "_" + addonList.sort((a, b) => a.id - b.id).map((x) => x.id).join("_") : ""}`

        if (shouldApplyInventoryLimit && quantity > 0 && isStockQuantityExceeded(cart, selectedInventory, nextQuantity, [selectedItem?.internalId, finalId].filter(Boolean))) {
            message?.error(stockQuantityErrorMessage(selectedInventory));
            return;
        }

        return setProductQyt(nextQuantity);
    };

    const closeModal = (state: boolean) => {
        setIsVariant({ ...Isvariant, showModal: state })
    }
    const themeBg = selectedStore?.storemeta?.settings?.shop_page_theme?.bg || "#0F6E56";

    return (
        <div>
            {showModal ? (
                <>
                    <div
                        className="fixed inset-0 z-50 bg-neutral-900/50 backdrop-blur-[1px]"
                        onClick={() => {
                            setIsVariant({ Isvariant: false, product: product, showModal: showModal, quantity: productQyt })
                        }}
                    />
                    <div
                        className="fixed inset-0 z-50 flex items-end md:items-center justify-center outline-none focus:outline-none font-manrope pointer-events-none"
                    >
                        <motion.div
                            initial={{ y: '100%', opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ ease: 'easeOut', duration: 0.3 }}
                            className="relative w-full md:max-w-md md:mx-4 pointer-events-auto"
                            onClick={e => e.stopPropagation()}
                        >
                            {/*content*/}
                            <div className="border-0 rounded-t-3xl md:rounded-2xl shadow-2xl relative flex flex-col w-full max-h-[88vh] md:max-h-[85vh] bg-white outline-none focus:outline-none overflow-hidden">
                                <div className="flex justify-center pt-2 pb-1 md:hidden">
                                    <div className="w-10 h-1 rounded-full bg-neutral-300"></div>
                                </div>
                                {/*header*/}
                                <div className="flex items-start justify-between px-4 pt-2 md:pt-4 pb-2 border-b border-neutral-100">
                                    <div className="flex flex-col gap-2 flex-1 min-w-0">
                                        <span className="text-base text-neutral-800 font-semibold select-none">Item Customization</span>
                                        {customizedItems?.filter((i) => i?.deleted != true)?.length > 0 &&
                                            <div className="flex flex-row gap-2 flex-wrap">
                                                {
                                                    customizedItems?.filter((i) => i?.deleted != true)?.map((cartItem, index) => {
                                                        return (
                                                            <div key={index} className={`flex gap-2 justify-center items-center rounded-full ${selectedItem?.internalId == cartItem?.internalId ? "bg-[#95B46A] text-white" : "text-success-500 border border-solid border-[#95B46A]"} overflow-hidden h-6`}>
                                                                <div className="flex flex-row gap-1 select-none h-full items-center pr-1">
                                                                    <span className={`flex items-center h-full ${selectedItem?.internalId == cartItem?.internalId ? "bg-success-500" : ""} text-xs pl-2 pr-1 rounded-full`}>Custom:</span>
                                                                    <span className="text-sm py-1">{cartItem?.quantity}</span>
                                                                    {cartItem?.addon?.length > 0 &&
                                                                        <span className="bg-success-400 text-white rounded-full px-2 text-xs">{cartItem?.addon?.length}</span>
                                                                    }
                                                                    <button className="p-0.5 text-superone" onClick={() => { if (cartItem?.internalId != selectedItem?.internalId) { setSelectedItem(cartItem) } else { setSelectedItem(null) } }}><Pencil size="12" /></button>
                                                                </div>
                                                            </div>)
                                                    })
                                                }
                                            </div>
                                        }
                                    </div>
                                    <button
                                        type="button"
                                        className="cursor-pointer p-1.5 -mt-1 -mr-1 rounded-full hover:bg-neutral-100 transition-colors flex-shrink-0"
                                        onClick={() => { closeModal(false); setSelectedItem(null) }}
                                    >
                                        <X size={20} className="text-neutral-500" />
                                    </button>
                                </div>
                                {/*body*/}
                                <div className="relative px-4 py-3 flex-auto overflow-y-auto">
                                    <div className="flex gap-3 items-center pb-3">
                                        <Image
                                            loader={sanityIoImageLoader}
                                            src={getImageObject(product.images)?.url || "https://jhattse.com/public/consumer/placeholder/product_square.png"}
                                            alt={product?.name || ''}
                                            width="64"
                                            height="64"
                                            className="rounded-xl object-cover flex-shrink-0"
                                        />
                                        <span className="text-neutral-800 font-semibold text-base line-clamp-2">{trimToLength(product?.name, 80)}</span>
                                    </div>
                                    {addonList?.length > 0 &&
                                        <div className="flex flex-row flex-wrap gap-2 items-center pb-3">
                                            {
                                                addonList?.filter((a) => a?.enable != false)?.map((addon, idx) => {
                                                    return (
                                                        <div className="flex gap-2 justify-center items-center py-1 px-2 rounded-full bg-success-500 text-white text-xs" key={idx}>
                                                            <span className="select-none">{addon?.addon?.product.name}</span>
                                                            <button className="cursor-pointer text-white" onClick={() => { toggleAddon(addon) }}>x</button>
                                                        </div>
                                                    )
                                                })
                                            }
                                        </div>}
                                    {
                                        product?.variants?.length > 0 &&
                                        <div className="pb-1">
                                            <h3 className="font-semibold text-sm text-neutral-500 uppercase tracking-wide py-2">Variants</h3>
                                            <div className="flex flex-col gap-2">
                                                {product?.variants?.length !== 0 && product?.variants?.map((item, index: number) => {
                                                    const isAvailable = getFirst(inventoryByVariantId(item, product))?.is_available || inventory?.is_available;
                                                    const isSelected = item?.id == variant?.id;
                                                    return (
                                                        <button
                                                            type="button"
                                                            key={index}
                                                            disabled={!isAvailable}
                                                            className={`flex items-center justify-between w-full text-left rounded-xl px-4 py-3 border-2 transition-colors ${!isAvailable
                                                                ? "bg-neutral-50 border-neutral-200 opacity-50 cursor-not-allowed"
                                                                : isSelected
                                                                    ? "bg-business-500/5 border-business-500 cursor-pointer"
                                                                    : "bg-white border-neutral-200 hover:border-business-500/40 cursor-pointer"
                                                                }`}
                                                            onClick={() => isAvailable && onClickVariant(item)}
                                                        >
                                                            <div className="flex items-center gap-3 min-w-0">
                                                                <span className={`flex items-center justify-center flex-shrink-0 w-5 h-5 rounded-full border-2 ${!isAvailable ? "border-neutral-300" : isSelected ? "border-business-500 bg-business-500" : "border-neutral-300"}`}>
                                                                    {isSelected && isAvailable && <Check size={12} className="text-white" strokeWidth={3} />}
                                                                </span>
                                                                <div className="flex flex-col min-w-0">
                                                                    <span className={`text-sm font-semibold line-clamp-1 ${!isAvailable ? "text-neutral-400" : "text-neutral-900"}`}>{trimToLength((item?.name || product?.name), 40)}</span>
                                                                    {!isAvailable && <span className="text-xs font-medium text-error-400">Out of stock</span>}
                                                                </div>
                                                            </div>
                                                            <span className={`text-sm font-bold flex-shrink-0 pl-2 ${!isAvailable ? "text-neutral-400" : "text-neutral-900"}`}>{humanizeCurrency(getFirst(inventoryByVariantId(item, product))?.price || getFirst(inventoryByVariantId(item, product))?.mrp || item?.mrp || 0)}</span>
                                                        </button>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    }
                                    {
                                        addons?.length > 0 &&
                                        <div className="pt-2">
                                            <h3 className="font-semibold text-sm text-neutral-500 uppercase tracking-wide py-2">Addons</h3>
                                            <div className="flex flex-col gap-2">
                                                {addons?.length !== 0 && addons?.map((item, index: number) => {
                                                    const isChecked = addonList?.filter((a) => a?.enable != false)?.some((e) => e?.addon_id === item?.addon_id);
                                                    return (
                                                        <button
                                                            type="button"
                                                            key={index}
                                                            className={`flex items-center justify-between w-full text-left rounded-xl px-4 py-3 border-2 transition-colors cursor-pointer ${isChecked ? "bg-business-500/5 border-business-500" : "bg-white border-neutral-200 hover:border-business-500/40"}`}
                                                            onClick={() => { toggleAddon(item) }}
                                                        >
                                                            <div className="flex items-center gap-3 min-w-0">
                                                                <span className={`flex items-center justify-center flex-shrink-0 w-5 h-5 rounded-md border-2 ${isChecked ? "border-business-500 bg-business-500" : "border-neutral-300"}`}>
                                                                    {isChecked && <Check size={12} className="text-white" strokeWidth={3} />}
                                                                </span>
                                                                <span className="text-sm font-semibold text-neutral-900 line-clamp-1">{trimToLength((item?.addon?.product?.name), 40)}</span>
                                                            </div>
                                                            <span className="text-sm font-bold text-neutral-900 flex-shrink-0 pl-2">{humanizeCurrency(item?.price)}</span>
                                                        </button>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    }
                                </div>
                                {/*footer*/}
                                <div className="flex items-center justify-center gap-3 px-4 py-3 border-t border-neutral-100 bg-white">
                                    <div className="flex items-center justify-between border-2 border-business-500 rounded-xl overflow-hidden flex-shrink-0">
                                        <button
                                            type="button"
                                            className="flex items-center justify-center p-2.5 text-business-500 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
                                            disabled={productQyt == 1}
                                            onClick={(event) => increase(-1)}
                                        >
                                            <Minus size={16} />
                                        </button>
                                        <span className="px-3 min-w-8 text-center font-semibold text-business-500">{productQyt}</span>
                                        <button
                                            type="button"
                                            className="flex items-center justify-center p-2.5 text-business-500 cursor-pointer"
                                            onClick={(event) => increase(1)}
                                        >
                                            <Plus size={16} />
                                        </button>
                                    </div>
                                    <button
                                        style={{ background: themeBg }}
                                        className="flex-1 text-sm py-3 px-3 text-neutral-50 rounded-xl font-semibold font-manrope cursor-pointer shadow-sm hover:brightness-110 transition-all"
                                        type="button"
                                        onClick={() => { onAddToCart(product, addonList, inventory || getFirst(inventoryByVariantId(variant, product)) as TInventory, productQyt, selectedItem?.internalId); setSelectedItem(null) }}
                                    >
                                        {`Add To Cart | ${humanizeCurrency(parseFloat(modalPriceCalculation(inventory || getFirst(inventoryByVariantId(variant, product)), productQyt, addonList)))}`}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </>
            ) : null}
        </div>
    )
}

export default Variant
